<template>
  <NuxtLayout name="home">
    <section class="home-hero" data-testid="hero-element">
      <div class="home-hero-copy">
        <h1 class="editorial-display">
          I help developers make <span class="whitespace-nowrap">AI-written</span> software <em>reliable.</em>
        </h1>
        <p class="editorial-dek">
          I share practical approaches to agentic coding, AI-assisted engineering, and software quality.
        </p>
        <div class="hero-actions">
          <NuxtLink to="/blog" class="editorial-pill primary">
            Explore the blog
            <span aria-hidden="true">↗</span>
          </NuxtLink>
          <NuxtLink to="/about" class="editorial-text-link">
            Learn more
            <span aria-hidden="true">↗</span>
          </NuxtLink>
        </div>
      </div>
      <EditorialPortrait />
    </section>
    <section class="home-latest" aria-labelledby="latest-title">
      <div class="section-heading">
        <div>
          <h2 id="latest-title">Latest articles</h2>
        </div>
        <NuxtLink to="/blog" class="editorial-text-link">All articles ↗</NuxtLink>
      </div>
      <div class="editorial-article-grid">
        <BlogItem v-for="post in posts" :key="post.slug" :item="post" />
      </div>
    </section>
    <section class="learn-together" aria-labelledby="learn-title">
      <div class="learn-intro">
        <h2 id="learn-title">Work with me</h2>
        <p>I run workshops, teach courses, and help teams improve their tests.</p>
        <NuxtLink to="/about" class="editorial-text-link">About me ↗</NuxtLink>
      </div>
      <div class="service-list">
        <NuxtLink
          v-for="(service, index) in services"
          :key="service.title"
          :to="service.link"
          class="service-row"
        >
          <span class="editorial-label">0{{ index + 1 }}</span>
          <div>
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
          </div>
          <span aria-hidden="true">↗</span>
        </NuxtLink>
      </div>
    </section>
  </NuxtLayout>
</template>
<script setup lang="ts">
import type { Article } from '~/types/article'
const { data: posts } = await useAsyncData('editorial-home-posts', () =>
  queryContent<Article>('/')
    .where({ published: true })
    .only(['title', 'slug', 'date', 'description', 'image', 'tags', 'readingTime'])
    .sort({ date: -1 })
    .limit(3)
    .find()
)
const services = [
  {
    title: 'Workshops',
    description: 'Learn by writing tests and working through examples with me.',
    link: '/workshops'
  },
  {
    title: 'Courses',
    description: 'Learn Cypress and web testing at your own pace.',
    link: '/courses'
  },
  {
    title: 'Consulting',
    description: 'Get help reviewing your test suite and improving your testing process.',
    link: '/company-consulting'
  },
  {
    title: 'Tutoring',
    description: 'Work through your testing challenges, one-on-one.',
    link: '/individual-tutoring'
  },
  {
    title: 'Conference talks',
    description: 'Talks on testing and web development, with live coding examples.',
    link: '/about#conference-appearances'
  }
]
useSeoMeta({
  title: 'Testing courses, workshops, and articles |',
  description:
    'Filip Hric helps developers build better software with practical articles, courses and workshops on testing, Cypress, Playwright and AI.'
})
defineOgImage({
  component: 'OgImageHome',
  alt: 'Filip Hric — I help developers make AI-written software reliable.'
})
</script>
